import { AlertCircle } from '@ethsign/icons';

import { Modal } from '@ethsign/ui';

import { getEthKeyPairFromString, hashSha256 } from '@ethsign/utils-web';
import { ethSignAesEncrypt } from '@ethsign/utils-web/dist/esm/ethsign';
import { useQuery } from '@tanstack/react-query';

import classNames from 'classnames';

import { Check, Loader, Loader2 } from 'lucide-react';

import React, { useEffect, useRef, useState } from 'react';

import { useUserInfo } from '@/hooks';
import { initUserIdentity, preparePasswordManager } from '@/services';
import { getNaNoId, getTextFromTemplate, getUserWalletType, WalletType } from '@/utils';
import { WalletBase } from '@/utils/wallet/WalletBase';
import { WalletFactory } from '@/utils/wallet/WalletFactory';

import { ColorAlertIcon } from '../Icons';

enum StepStatusEnum {
  UNACTIVE = 'unactive',
  ACTIVE = 'active',
  SUCCESS = 'success',
  FAIL = 'fail'
}

const StepIcon = ({ status }: { status: StepStatusEnum }) => {
  if (status === StepStatusEnum.UNACTIVE) {
    return (
      <div className={'w-8 h-8 flex justify-center items-center bg-gray-50 rounded-full'}>
        <div className={'w-[10px] h-[10px] rounded-full bg-gray-200'}></div>
      </div>
    );
  }
  if (status === StepStatusEnum.ACTIVE) {
    return <Loader size={32} className={'animate-spin'} color={'#DD8D58'} />;
  }
  if (status === StepStatusEnum.SUCCESS) {
    return (
      <div className={'w-8 h-8 flex justify-center items-center bg-primary-50 rounded-full'}>
        <Check color={'#DD8D58'} />
      </div>
    );
  }
  if (status === StepStatusEnum.FAIL) {
    return <AlertCircle size={32} color={'#F04438'} />;
  }
};

export const PasswordManagerModal = ({
  open,
  onOpenChange,
  steps,
  onConfirm
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  steps: {
    step1: StepStatusEnum;
    step2: StepStatusEnum;
  };
  onConfirm: () => void;
}) => {
  return (
    <>
      <Modal
        hiddenCloseIcon
        open={open}
        onOpenChange={onOpenChange}
        className={'md:w-[475px] max-w-max sm:rounded-xl'}
        confirmText={'Retry'}
        cancelText={'Cancel'}
        confirmButtonProps={{
          variant: 'primary'
        }}
        onConfirm={onConfirm}
        footer={steps.step1 === StepStatusEnum.FAIL || steps.step2 === StepStatusEnum.FAIL ? undefined : false}
      >
        <div className="flex items-center flex-col justify-center">
          {steps.step1 === StepStatusEnum.ACTIVE || steps.step2 === StepStatusEnum.ACTIVE ? (
            <Loader2 size={32} color={'#DD8D58'} className={'animate-spin'} />
          ) : (
            <ColorAlertIcon />
          )}
          <div className="text-lg font-medium text-gray-900 mt-[20px] text-center">
            Enabling EthSign Password Manager
          </div>
          <div className="text-sm text-gray-500 mt-[8px] mb-[12px] text-left flex">
            <div>
              <div className={'flex gap-4'}>
                <div className={'relative'}>
                  <StepIcon status={steps.step1} />
                  <div
                    className={classNames('w-[2px] h-[62px] bg-gray-200 rounded-sm left-[16px] top-[34px] absolute', {
                      'bg-primary': steps.step1 === StepStatusEnum.SUCCESS
                    })}
                  ></div>
                </div>
                <div>
                  <div className={'text-gray-700 text-md font-medium'}>Generate encryption keypair</div>
                  <div className="text-gray-500 text-md pb-6">
                    Generating your encryption keypair from a random signature.
                  </div>
                </div>
              </div>
              <div className={'flex gap-4'}>
                <div>
                  <StepIcon status={steps.step2} />
                </div>
                <div>
                  <div className={'text-gray-700 text-md font-medium'}>Sign your generated keypair</div>
                  <div className="text-gray-500 text-md">
                    Signing your generated keypair to validate its integrity and uploading your encryption public key.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

const initStatus = {
  step1: StepStatusEnum.UNACTIVE,
  step2: StepStatusEnum.UNACTIVE
};
const initSeedState = {
  seed: '',
  seedMessage: '',
  seedSignature: ''
};

export const usePasswordManagerModal = ({ onSuccess }: { onSuccess?: () => void }) => {
  const { user } = useUserInfo();
  const [steps, setSteps] = useState(initStatus);
  const seedRef = useRef(initSeedState);
  const walletRef = useRef<WalletBase>();
  const keypairRef = useRef<{ publicKey: string; privateKey: string }>();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['preparePasswordManager'],
    queryFn: preparePasswordManager
  });

  useEffect(() => {
    if (!open) {
      setSteps(initStatus);
      seedRef.current = initSeedState;
    }
  }, [open]);

  const getSeedSignature = async () => {
    const wallet = walletRef.current!;
    setSteps((prev) => ({
      ...prev,
      step1: StepStatusEnum.ACTIVE
    }));
    const seed = getNaNoId();
    const seedMessage = getTextFromTemplate(data?.identitySignMessageTemplate || '', { randomSeed: seed });
    try {
      const { signature } = await wallet.safeSign(seedMessage);
      seedRef.current = {
        seed,
        seedMessage: seedMessage,
        seedSignature: signature
      };
      setSteps((prev) => ({
        ...prev,
        step1: StepStatusEnum.SUCCESS
      }));
      signKeyPair();
    } catch (e) {
      setSteps((prev) => ({
        ...prev,
        step1: StepStatusEnum.FAIL
      }));
    }
  };

  const signKeyPair = async () => {
    const keypair = keypairRef.current;
    const wallet = walletRef.current!;
    setSteps((prev) => {
      return {
        ...prev,
        step2: StepStatusEnum.ACTIVE
      };
    });
    const seedState = seedRef.current!;
    try {
      const encryptedPK = await ethSignAesEncrypt(keypair!.privateKey, seedState.seedSignature.slice(0, 32));
      const verifyMessage = getTextFromTemplate(data!.identityVerifyMessageTemplate!, {
        publicKey: keypair!.publicKey,
        randomSeed: seedState.seed,
        encryptedPK
      });
      const { message, signature } = await wallet.safeSign(verifyMessage);
      await initUserIdentity({
        message,
        signature,
        publicKey: keypair!.publicKey,
        randomSeed: seedState.seedMessage,
        randomSeedValue: seedState.seed,
        encryptedPK,
        identityType: 'wallet',
        tag: wallet.protocolTag
      });
      onSuccess?.();
      setOpen(false);
    } catch (e) {
      console.error(e);
      setSteps((prev) => {
        return {
          ...prev,
          step2: StepStatusEnum.FAIL
        };
      });
    }
  };

  const signByMasterPassword = async (masterPassword: string) => {
    const keypair = keypairRef.current;
    const wallet = walletRef.current!;

    const seed = getNaNoId();
    const seedMessage = getTextFromTemplate(data?.identitySignMessageTemplate || '', { randomSeed: seed });

    const encryptedPK = await ethSignAesEncrypt(keypair!.privateKey, masterPassword);

    const verifyMessage = getTextFromTemplate(data!.identityVerifyMessageTemplate!, {
      publicKey: keypair!.publicKey,
      randomSeed: seed,
      encryptedPK
    });
    const walletType = getUserWalletType(user!.chainType as WalletType, user!.authType);
    let signData: any;
    if ([WalletType.StarkNet].includes(walletType as any) && wallet.signByEIP712) {
      const signatureDataHash = await hashSha256(verifyMessage);
      const eip712Message = wallet.getSignEIP712Message({
        chainId: wallet.chainId,
        info: 'PasswordManager',
        hash: signatureDataHash
      });
      signData = await wallet.signByEIP712(eip712Message);
    } else {
      signData = await wallet.safeSign(verifyMessage);
    }
    const { message, signature } = signData;
    const res = await initUserIdentity({
      message,
      signature,
      publicKey: keypair!.publicKey,
      randomSeed: seedMessage,
      randomSeedValue: seed,
      encryptedPK,
      identityType: 'password',
      tag: wallet.protocolTag
    });
    console.log(res);
    onSuccess?.();
  };
  const enablePasswordManager = async (masterPassword?: string) => {
    const keypair = getEthKeyPairFromString(getNaNoId());
    keypairRef.current = keypair;
    const walletType = getUserWalletType(user!.chainType as WalletType, user!.authType);
    const wallet = await WalletFactory.getWallet(walletType, user?.displayAddress);
    walletRef.current = wallet;
    const isConnected = await wallet.connect(user!.authType);
    if (!isConnected) return false;
    if (masterPassword) {
      await signByMasterPassword(masterPassword);
    } else {
      setOpen(true);
      await getSeedSignature();
    }
  };

  const onRetry = async () => {
    if (steps.step1 === StepStatusEnum.FAIL) {
      await getSeedSignature();
    }
    if (steps.step2 === StepStatusEnum.FAIL) {
      await signKeyPair();
    }
  };

  return {
    onEnable: enablePasswordManager,
    open,
    setOpen,
    steps,
    onRetry
  };
};
